/**
 * Buyer Analysis Card Component
 *
 * Displays buyer type analysis including:
 * - Valuation by buyer type
 * - Buyer rankings
 * - Buyer characteristics
 */

import React from 'react';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

function BuyerAnalysisCard({ buyerAnalysis }) {
  if (!buyerAnalysis) return null;

  const { byType, rankings } = buyerAnalysis;

  return (
    <div className="buyer-analysis-card">
      <h2>Buyer Type Analysis</h2>
      <p className="section-description">
        Compare potential valuations and characteristics across different buyer types
      </p>

      {/* Rankings */}
      <div className="rankings-section">
        <h3>Buyer Rankings</h3>
        <div className="rankings-list">
          {rankings.map((ranking) => (
            <div key={ranking.buyerType} className={`ranking-item rank-${ranking.rank}`}>
              <div className="rank-badge">#{ranking.rank}</div>
              <div className="rank-content">
                <h4>{ranking.name}</h4>
                <span className={`recommendation ${ranking.recommendation.toLowerCase()}`}>
                  {ranking.recommendation}
                </span>
              </div>
              <div className="rank-value">
                {formatCurrency(ranking.valuation)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Buyer Type Details */}
      <div className="buyer-types-grid">
        {Object.entries(byType).map(([type, data]) => (
          <div key={type} className={`buyer-type-card ${type}`}>
            <div className="buyer-header">
              <h4>{data.name}</h4>
              <p className="buyer-description">{data.description}</p>
            </div>

            <div className="buyer-valuation">
              <div className="valuation-row">
                <span>Base Value</span>
                <span>{formatCurrency(data.valuation.base)}</span>
              </div>
              {data.valuation.synergies > 0 && (
                <div className="valuation-row synergies">
                  <span>+ Synergies</span>
                  <span>{formatCurrency(data.valuation.synergies)}</span>
                </div>
              )}
              <div className="valuation-row total">
                <span>Total Value</span>
                <span>{formatCurrency(data.valuation.total)}</span>
              </div>
              <div className="valuation-row multiple">
                <span>Multiple</span>
                <span>{data.valuation.multiple}x EBITDA</span>
              </div>
            </div>

            <div className="buyer-details">
              <div className="detail-item">
                <span className="label">Due Diligence</span>
                <span className="value">{data.dueDiligenceDays} days</span>
              </div>
              <div className="detail-item">
                <span className="label">Financing</span>
                <span className="value">{formatFinancingType(data.financingType)}</span>
              </div>
              {data.rolloverExpected > 0 && (
                <div className="detail-item">
                  <span className="label">Rollover Expected</span>
                  <span className="value">{Math.round(data.rolloverExpected * 100)}%</span>
                </div>
              )}
              <div className="detail-item">
                <span className="label">Earnout Likelihood</span>
                <span className="value">{Math.round(data.earnoutLikelihood * 100)}%</span>
              </div>
            </div>

            <div className="buyer-characteristics">
              <h5>Characteristics</h5>
              <ul>
                {data.characteristics?.slice(0, 3).map((char, idx) => (
                  <li key={idx}>{char}</li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatFinancingType(type) {
  const types = {
    leveraged: 'Leveraged (Debt)',
    cash_or_stock: 'Cash or Stock',
    seller_financed: 'Seller Financed'
  };
  return types[type] || type;
}

export default BuyerAnalysisCard;
